if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.multiVariantSelects = this.querySelector('multi-variant-selects');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initMultiVariantHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initMultiVariantHandlers() {
        if (!this.multiVariantSelects) return;

        this.multiVariantSelects.addEventListener('change', this.handleMultiVariantChange.bind(this));
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
        
        // Add event listener for quantity changes
        this.quantityInput.addEventListener('change', () => {
          this.updatePriceBasedOnQuantity();
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleMultiVariantChange(event) {
        const selectedVariants = Array.from(this.multiVariantSelects.querySelectorAll('input[type="checkbox"]:checked'));
        const totalPrice = selectedVariants.reduce((acc, variant) => {
          const price = parseFloat(variant.dataset.price);
          return acc + price;
        }, 0);

        const formattedPrice = window.Currency.formatMoney(totalPrice, window.currency.currencyFormat);

        const regularPriceDisplay = this.querySelector('.price-item--regular');
        if (regularPriceDisplay) {
          regularPriceDisplay.innerHTML = formattedPrice;
        }

        const salePriceDisplay = this.querySelector('.price-item--sale');
        if (salePriceDisplay) {
          salePriceDisplay.innerHTML = formattedPrice;
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

        resetProductFormState() {
            const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        if (requestUrl) console.log('[product-info] renderProductInfo', requestUrl);
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          html.querySelectorAll('[id^="MachineCompatibility-"]').forEach((sourceEl) => {
            const destinationEl = this.querySelector(`#${sourceEl.id}`);
            if (destinationEl) {
              destinationEl.innerHTML = sourceEl.innerHTML;
            }
          });

          updateSourceFromDestination('price');
          updateSourceFromDestination('product-form-buttons');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('MediaWrapper');
          
          this.updateProductSpecs(productUrl, variant?.id);

          this.updateConfiguratorOptions(variant);

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          
          // Only update price based on quantity if we have a quantity input
          if (this.quantityInput && this.quantityInput.value > 1) {
            this.updatePriceBasedOnQuantity();
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateProductSpecs(productUrl, variantId) {
        const productSpecsElement = document.querySelector('[id^="product-specs-"]');
        if (!productSpecsElement || !productSpecsElement.dataset.section) return;

        const specsSectionId = productSpecsElement.dataset.section;
        const url = variantId
          ? `${productUrl}?variant=${variantId}&section_id=${specsSectionId}`
          : `${productUrl}?section_id=${specsSectionId}`;

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newSpecsSection = html.querySelector('.shopify-section');
            if (newSpecsSection) {
              const oldSpecsSection = document.getElementById(`shopify-section-${specsSectionId}`);
              if (oldSpecsSection) {
                oldSpecsSection.innerHTML = newSpecsSection.innerHTML;
              }
            }
          });
      }

      updateConfiguratorOptions(variant) {
        const configurator = this.querySelector('product-configurator');
        if (configurator && variant) {
          configurator.dataset.price = variant.price;
          const firstDetails = configurator.querySelector('details');
          if (firstDetails) {
            firstDetails.open = true;
          }
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

        setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html) {
        const newModal = html.querySelector(`#ProductModal-${this.sectionId}`);
        const modal = this.productModal;

        if (newModal && modal) {
          modal.innerHTML = newModal.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
        
        // Update price after quantity rules are updated
        this.updatePriceBasedOnQuantity();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }


      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      updatePriceBasedOnQuantity() {
        if (!this.quantityInput) return;
        
        // Get the current price from the price element that was just updated by the variant change
        const priceElement = this.querySelector('[data-price]');
        if (!priceElement) return;
        
        // Get the current variant's price
        const basePrice = parseInt(priceElement.dataset.price, 10);
        const quantity = parseInt(this.quantityInput.value, 10);
        
        if (isNaN(basePrice) || isNaN(quantity)) return;
        
        // Find the quantity table and get the discount data
        const quantityTable = this.querySelector('table-quantity');
        let discountedPrice = basePrice;
        let discountPercentage = 0;
        
        if (quantityTable) {
          // Get the discount data from the script tag
          const discountScript = quantityTable.querySelector('script[type="application/json"]');
          if (discountScript && discountScript.textContent) {
            try {
              const discountData = JSON.parse(discountScript.textContent);
              
              // Find the appropriate discount for the current quantity
              // Sort the quantities in descending order to find the highest applicable discount
              const sortedQuantities = Object.keys(discountData)
                .map(qty => parseInt(qty, 10))
                .sort((a, b) => b - a);
              
              // Find the first quantity that is less than or equal to the current quantity
              const applicableQuantity = sortedQuantities.find(qty => quantity >= qty);
              
              if (applicableQuantity) {
                // Get the discount percentage for this quantity
                discountPercentage = parseFloat(discountData[applicableQuantity]);
                
                // Calculate the discounted price
                const discountAmount = basePrice * (discountPercentage / 100);
                discountedPrice = basePrice - discountAmount;
              }
            } catch (e) {
              console.error('Error parsing discount data:', e);
              // Continue with base price if there's an error
            }
          }
        }
        
        // Calculate the total price with the discounted price per item
        const totalPrice = discountedPrice * quantity;
        
        // Update the price display
        const priceDisplay = this.querySelector('.price-item--regular');
        if (priceDisplay) {
          priceDisplay.innerHTML = window.Currency.formatMoney(totalPrice, window.currency.currencyFormat);
        }
        
        // Also update the sale price if it exists
        const salePriceDisplay = this.querySelector('.price-item--sale');
        if (salePriceDisplay) {
          salePriceDisplay.innerHTML = window.Currency.formatMoney(totalPrice, window.currency.currencyFormat);
        }
        
        // Update the selected row in the quantity table if it exists
        if (quantityTable) {
          const rows = quantityTable.querySelectorAll('tbody tr');
          if (rows && rows.length > 0) {
            rows.forEach(row => {
              const rowQuantity = parseInt(row.getAttribute('data-quantity'), 10);
              if (!isNaN(rowQuantity) && rowQuantity === quantity) {
                row.classList.add('selected');
              } else {
                row.classList.remove('selected');
              }
            });
          }
        }
      }
    }
  );
}